import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { huntingApi } from '../services/api';
import DayCard from '../components/DayCard';
import LocationSwitcher from '../components/LocationSwitcher';
import HomeView from '../components/HomeView';
import { formatOutlookDate } from '../services/forecastDates';

interface FactorScores {
  temperature: number;
  front: number;
  wind: number;
  precipitation: number;
  clouds: number;
  pressure: number;
  moon: number;
  lunarFeeding: number;
}

interface PeriodConditions {
  tempF: number;
  windSpeedMph: number;
  windGustMph: number;
  cloudPercent: number;
  precipitationType: string;
  pressureTrend: string;
  frontType: string;
}

export type IndicatorStatus = 'good' | 'neutral' | 'bad';

export interface WeatherIndicators {
  temperature: IndicatorStatus;
  wind: IndicatorStatus;
  gusts: IndicatorStatus;
  clouds: IndicatorStatus;
  pressure: IndicatorStatus;
  precipitation: IndicatorStatus;
  front: IndicatorStatus;
}

export interface PeriodForecast {
  period: 'morning' | 'evening';
  rating: number;
  weightedScore: number;
  factors: FactorScores;
  conditions: PeriodConditions;
  briefing?: { opening: string; explanation: string[]; takeaway: string };
  indicators?: WeatherIndicators;
}

export interface DayForecast {
  date: string;
  dayOfWeek: string;
  overallRating: number;
  sunrise: string;
  sunset: string;
  lunarPhase: string;
  lunarIllumination: number;
  morning: PeriodForecast;
  evening: PeriodForecast;
  morningSummary?: string;
  eveningSummary?: string;
}

interface LockedDayForecast {
  date: string;
  dayOfWeek: string;
  locked: true;
}

export interface HuntingOutlook {
  zipcode: string;
  generatedAt: string;
  timezone?: string;
  utcOffsetSeconds?: number;
  forecasts: (DayForecast | LockedDayForecast)[];
}

function isLocked(forecast: DayForecast | LockedDayForecast): forecast is LockedDayForecast {
  return 'locked' in forecast && forecast.locked === true;
}

function formatForecastDate(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function DashboardPage({ view = 'outlook' }: { view?: 'home' | 'outlook' }) {
  const { user, activeLocation } = useAuth();
  const navigate = useNavigate();
  const [outlook, setOutlook] = useState<HuntingOutlook | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [locationName, setLocationName] = useState('');
  const [retry, setRetry] = useState(0);
  const zipcode = activeLocation?.zipcode;
  const tier = user?.tier;
  const refreshHome = useCallback(() => setRetry(value => value + 1), []);

  useEffect(() => {
    if (!zipcode) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setOutlook(null);
    setLocationName('');
    const fetchOutlook = async () => {
      try {
        const response = await huntingApi.getOutlook(zipcode);
        const result: HuntingOutlook = response.data;
        if (!result.forecasts.length || isLocked(result.forecasts[0])) {
          throw new Error('Forecast response is missing today');
        }
        if (!cancelled) setOutlook(result);
      } catch (err) {
        console.error('Outlook fetch error:', err);
        if (!cancelled) setError('Failed to load hunting outlook. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const fetchLocation = async () => {
      try {
        const response = await huntingApi.getLocation(zipcode);
        if (!cancelled && response.data.city && response.data.stateAbbr) {
          setLocationName(`${response.data.city}, ${response.data.stateAbbr}`);
        }
      } catch (err) {
        console.error('Forecast location lookup failed; displaying ZIP code:', err);
      }
    };
    void fetchOutlook();
    void fetchLocation();
    return () => { cancelled = true; };
  }, [zipcode, tier, retry]);

  useEffect(() => {
    const loadedAt = Date.now();
    const refresh = () => {
      if (document.visibilityState === 'visible' && Date.now() - loadedAt >= 300_000) {
        setRetry(value => value + 1);
      }
    };
    document.addEventListener('visibilitychange', refresh);
    return () => document.removeEventListener('visibilitychange', refresh);
  }, [retry, zipcode, tier]);

  const openFreeVsPlus = () => {
    navigate('/support', { state: { tab: 'free-vs-plus' } });
  };

  const handleDayClick = (date: string) => {
    navigate(`/daily?date=${date}`);
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading" role="status">
          <div className="loading-spinner" aria-hidden="true"></div>
          <p>Fetching weather data and calculating your hunting forecast...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={() => setRetry(value => value + 1)} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!outlook) return null;

  const displayName = user?.nickname || user?.email?.split('@')[0] || 'Hunter';
  const locationLabel = <LocationSwitcher fallbackLabel={locationName || zipcode || ''} />;
  const today = outlook.forecasts[0];
  if (view === 'home' && today && !isLocked(today)) {
    return (
      <HomeView
        key={`${outlook.zipcode}-${today.date}`}
        outlook={outlook}
        onRefresh={refreshHome}
        header={<OutlookHeader displayName={displayName} locationLabel={locationLabel} locationCity={activeLocation?.name ? locationName : undefined} />}
      />
    );
  }

  return (
    <OutlookView
      outlook={outlook}
      displayName={displayName}
      tier={user?.tier}
      locationLabel={locationLabel}
      locationCity={activeLocation?.name ? locationName : undefined}
      onDayClick={handleDayClick}
      onUpgrade={openFreeVsPlus}
      hideWelcome
      hideTodayHeading
      hideUpcomingHeading
      formatDayDate={formatOutlookDate}
    />
  );
}

interface OutlookViewProps {
  outlook: HuntingOutlook;
  displayName: string;
  tier?: string;
  locationLabel: React.ReactNode;
  locationCity?: string;
  onDayClick: (date: string) => void;
  onUpgrade: () => void;
  hideTodayHeading?: boolean;
  hideWelcome?: boolean;
  hideUpcomingHeading?: boolean;
  formatDayDate?: (date: string) => string;
}

export function OutlookHeader({ displayName, locationLabel, locationCity, hideWelcome = false, dateLabel }: {
  displayName: string;
  locationLabel: React.ReactNode;
  locationCity?: string;
  hideWelcome?: boolean;
  dateLabel?: React.ReactNode;
}) {
  return (
    <section className="dashboard-header" aria-label="Hunting overview">
      <div className="today-overview">
        {!hideWelcome && (
          <h2>
            Welcome, {displayName}
          </h2>
        )}
        <p className="dashboard-subtitle">
          Hunting Outlook
          {locationLabel && (
            <>
              {' for '}
              {locationLabel}
              {locationCity && (
                <>
                  {'\u00a0· '}
                  <span className="location-city">{locationCity}</span>
                </>
              )}
            </>
          )}
        </p>
        {dateLabel}
      </div>
    </section>
  );
}

export function OutlookView({
  outlook, displayName, tier, locationLabel, locationCity, onDayClick, onUpgrade,
  hideTodayHeading = false, hideWelcome = false, hideUpcomingHeading = false,
  formatDayDate,
}: OutlookViewProps) {
  const todayForecast = outlook.forecasts[0];
  const upcomingForecasts = outlook.forecasts.slice(1);
  const hasLockedDays = outlook.forecasts.some(isLocked);

  return (
    <div className={`dashboard${hideUpcomingHeading ? ' outlook-continuous' : ''}`}>
      <OutlookHeader displayName={displayName} locationLabel={locationLabel} locationCity={locationCity} hideWelcome={hideWelcome} />

      <section className="today-section" aria-label="Today's forecast">
        {!hideTodayHeading && <h3 className="section-title">Today's Forecast</h3>}
        {todayForecast && !isLocked(todayForecast) && (
          <DayCard forecast={todayForecast} isToday={true} onDayClick={onDayClick} dateLabel={formatDayDate?.(todayForecast.date)} />
        )}
      </section>

      <section className="upcoming-section" aria-label="Upcoming forecasts">
        {!hideUpcomingHeading && <h3 className="section-title">Next {upcomingForecasts.length} Days</h3>}
        {tier === 'plus' && !hideUpcomingHeading && (
          <p className="form-hint">
            Future scores and predictions are based on weather data that can change rapidly. Conditions and scores may change as dates approach.
          </p>
        )}
        <div className="forecast-grid">
          {upcomingForecasts.map((forecast) =>
            isLocked(forecast) ? (
              <div
                key={forecast.date}
                className="day-card day-card-locked"
                role="link"
                tabIndex={0}
                aria-label={`${forecast.dayOfWeek} is locked. Learn about Free vs Plus accounts`}
                onClick={onUpgrade}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onUpgrade(); } }}
              >
                <div className="day-card-header">
                  <h3 className="day-name">{forecast.dayOfWeek}</h3>
                  <span className="day-date">{(formatDayDate || formatForecastDate)(forecast.date)}</span>
                </div>
                <p className="day-card-lock-icon" aria-hidden="true">🔒</p>
                <p className="day-card-lock-text">Upgrade to Plus for more — see what Plus includes</p>
              </div>
            ) : (
              <DayCard key={forecast.date} forecast={forecast} isToday={false} onDayClick={onDayClick} dateLabel={formatDayDate?.(forecast.date)} />
            )
          )}
        </div>
        {tier === 'plus' && hideUpcomingHeading && (
          <p className="form-hint">
            Future scores and predictions are based on weather data that can change rapidly. Conditions and scores may change as dates approach.
          </p>
        )}
        {hasLockedDays && (
          <p className="upgrade-note">
            Free accounts see the next 3 days. Plus unlocks the full 7-day outlook and extended monthly
            weather detail.
          </p>
        )}
      </section>

    </div>
  );
}

export default DashboardPage;
